/**
 * @file 文件列表
 */
import {FC, Fragment} from 'react';
import cx from 'classnames';

import Icon, {IconSize} from '@/components/Icon';
import Tooltip, {EllipsisTooltip} from '@/components/Tooltip';
import {
    ImportErrorSvg2,
    RetrySvg,
    DialogCollapseSvg,
    ImportSuccessSvg2,
    DocumentSvg,
    SheetSvg,
    PPTSvg,
    DataSheetSvg,
} from '@/icons';
import {FileItem} from '@/types/document/file';
import {FileStatus, FileType} from '@/constants/document/file';
import {useUploadFile} from '@/hooks/document/useUploadFile';
import {ENVIRONMENT_LEVEL} from '@/Constant';
import {htmlUnescape, getFileName} from '@/utils/document/file';

import styles from './index.module.scss';

const Radius = 7; // 圆环半径
const CirclePerimeter = 2 * Math.PI * Radius; // 圆环周长

/**
 * 文件名是否需要最大宽度（操作项仅有一个时）
 * @param status 文件状态
 * @returns 文件名最大宽度
 */
const isNeedLongerNameWidth = (status: FileStatus): boolean => {
    return [FileStatus.Success, FileStatus.Error].includes(status);
};

interface FileListProps {
    fileList: FileItem[];
}

const FileList: FC<FileListProps> = ({fileList}) => {
    const {handleRetry} = useUploadFile();

    const handleClickRetry = (file: FileItem) => {
        handleRetry(file);
    };

    const handleOpenPage = (docGuid: string, e?: React.MouseEvent) => {
        e && e.stopPropagation();

        let origin = window.location.origin;
        if (process.env.REACT_APP_ENVIRONMENT === ENVIRONMENT_LEVEL.local) {
            origin = 'https://ku.dev.weiyun.baidu.com';
        }
        const shortUrl = `${origin}/d/${docGuid}`;
        window.open(shortUrl);
    };

    const handleClickItem = (file: FileItem) => {
        const {status, docGuid} = file;

        if (status === FileStatus.Success) {
            handleOpenPage(docGuid as string);
        }
    };

    const renderStatus = (file: FileItem) => {
        const {status, errorMsg} = file;
        let text: any = '';
        switch (status) {
            case FileStatus.Init:
                text = '等待导入';
                break;
            case FileStatus.Progress:
                text = '导入中...';
                break;
            case FileStatus.Success:
                text = (
                    <>
                        <Icon size={IconSize.LG} svg={ImportSuccessSvg2} noSpace />
                        <span className={styles.success}>导入完成</span>
                    </>
                );
                break;
            case FileStatus.Error:
                text = (
                    <>
                        <Icon size={IconSize.LG} svg={ImportErrorSvg2} noSpace />
                        <EllipsisTooltip
                            className={styles.tooltip}
                            content={errorMsg}
                            placement={'top'}
                        >
                            <span className={styles.error}>{errorMsg}</span>
                        </EllipsisTooltip>
                    </>
                );
                break;
            default:
                break;
        }
        return text;
    };

    const renderProgress = (progress: number) => {
        const percent = progress / 100;
        return (
            <>
                <span className={styles.progress}>{`${progress}%`}</span>
                <svg width="20" height="20" viewBox="0 0 20 20">
                    <circle cx="10" cy="10" r={Radius} strokeWidth="1.75" stroke="#EDF0F5" fill="none" />
                    <circle
                        cx="10"
                        cy="10"
                        r={Radius}
                        strokeWidth="1.75"
                        stroke="#5CC58F"
                        fill="none"
                        transform="matrix(0,-1,1,0,0,20)"
                        strokeDasharray={`${CirclePerimeter * percent} ${CirclePerimeter * (1 - percent)}`}
                    />
                </svg>
            </>
        );
    };

    const renderOperate = (file: FileItem) => {
        const {status, progress, docGuid} = file;

        switch (status) {
            case FileStatus.Init:
            case FileStatus.Progress:
                return renderProgress(progress);
            case FileStatus.Success:
                return (
                    <Icon
                        className={cx(styles.icon, styles.rotate)}
                        onClick={(e) => handleOpenPage(docGuid as string, e)}
                        size={IconSize.XL}
                        svg={DialogCollapseSvg}
                        noSpace
                    />
                );
            case FileStatus.Error:
                return (
                    <Tooltip placement={'left'} content={'重试'}>
                        <Icon
                            className={styles.icon}
                            onClick={() => handleClickRetry(file)}
                            size={IconSize.XL}
                            svg={RetrySvg}
                            noSpace
                        />
                    </Tooltip>
                );
        }
        return null;
    };

    const renderFileIcon = (fileType: FileType) => {
        switch (fileType) {
            case FileType.Excel:
                return SheetSvg;
            case FileType.ExcelDst:
                return DataSheetSvg;
            case FileType.PPT:
                return PPTSvg;
            case FileType.Word:
            case FileType.PDF:
                return DocumentSvg;
        }
        return DocumentSvg;
    };

    return (
        <div className={styles.content}>
            {fileList.map((file, mapIndex) => {
                const {docGuid, status, target: {name}, fileType, options} = file;
                const docName = options?.docName || ''; // 客户端导入文件的文件名
                const fileName = options?.fid // 展示的文件名
                    ? docName
                    : status === FileStatus.Success ? getFileName(name) : name;

                return (
                    <Fragment key={docGuid}>
                        <div
                            className={cx(styles.item, status === FileStatus.Success && styles.success)}
                            onClick={() => handleClickItem(file)}
                        >
                            <Icon className={styles.typeIcon} svg={renderFileIcon(fileType)} noSpace />
                            <div className={styles.info}>
                                <EllipsisTooltip
                                    className={styles.tooltip}
                                    content={fileName}
                                    placement={'top'}
                                    innerStyle={{
                                        fontSize: '14px',
                                        maxWidth: isNeedLongerNameWidth(status) ? '290px' : '260px',
                                    }}
                                >
                                    {htmlUnescape(fileName).slice(0, 100)}
                                </EllipsisTooltip>
                                <div className={styles.status}>{renderStatus(file)}</div>
                            </div>
                            <div className={styles.operate}>{renderOperate(file)}</div>
                        </div>
                        {mapIndex < fileList.length - 1 && <div className={styles.splitLine} />}
                    </Fragment>
                );
            })}
        </div>
    );
};

export default FileList;
